import { useCallback, useEffect, useMemo, useState } from "react";
import { generatePath, useMatches } from "react-router-dom";
import { useLazyQuery, useQuery } from "@apollo/client/react";

import {
  AssetSelectionDocument,
  AssetSelectionFilterDocument,
  RiskManagementOverallComparisonChartDocument,
  RiskManagementMajorEventComparisonChartDocument,
  RiskMitigationsDocument,
  WellNamesGroupedByHierarchyDocument,
} from "$/__generated__/graphql";
import {
  RISK_ROUTE,
  RISK_ROUTE_CHARTS_OVERALL,
  RISK_ROUTE_CHARTS_MAJOR,
} from "constants/routing";

import { GlobalFiltersContext } from "./context";
import {
  deriveRadioTreeBreadcrumbs,
  flattenCheckboxValues,
  flattenRadioState,
  generateRadioState,
  initHierarchyIdsCheckedState,
  initSelectedRiskAssetFiltersState,
  joinHierarchyCheckboxesWithWellIds,
  makeFilterItemProps,
  removeNodes,
} from "./helpers";
import type {
  FilteredWellNamesByHierarchy,
  GlobalFilters,
  GlobalFiltersProviderProps,
  RiskAssetFiltersState,
} from "./types";
import type { FilterItemProps } from "@jana-technology/platform-ui";

const baseRiskAssetFiltersState = {
  mostRecentCorrosionLogs: [],
  wellAgeRange: [],
  wellConfigurations: [],
  wellTypes: [],
  riskQuartiles: [],
} satisfies RiskAssetFiltersState;

export const GlobalFiltersProvider = ({
  children,
}: GlobalFiltersProviderProps) => {
  const allMatches = useMatches();

  const riskRoutes = allMatches.filter((match) =>
    match.pathname.startsWith(RISK_ROUTE),
  );

  const isRiskRoute = riskRoutes.length > 0;
  const isOverallRoute = allMatches.some(
    (match) => match.pathname === RISK_ROUTE_CHARTS_OVERALL,
  );
  const isMajorEventRoute = allMatches.some(
    (match) => match.pathname === RISK_ROUTE_CHARTS_MAJOR,
  );

  const isOverall = isOverallRoute;

  // #region Asset Selection
  const assetSelectionQueryResult = useQuery(AssetSelectionDocument);

  const hierarchyCheckboxItemsOverview = useMemo(() => {
    if (assetSelectionQueryResult.data?.hierarchies) {
      return makeFilterItemProps(assetSelectionQueryResult.data.hierarchies);
    }

    return [];
  }, [assetSelectionQueryResult.data]);

  const hierarchyCheckboxItemsRisk = useMemo(() => {
    if (assetSelectionQueryResult.data?.hierarchies) {
      return makeFilterItemProps(assetSelectionQueryResult.data.hierarchies);
    }

    return [];
  }, [assetSelectionQueryResult.data]);

  // these affect the query variables wherever filtering applies
  const [hierarchyIdsOverview, setHierarchyIdsOverview] =
    useState<GlobalFilters["hierarchyIdsOverview"]>(null);
  const [hierarchyIdsRisk, setHierarchyIdsRisk] =
    useState<GlobalFilters["hierarchyIdsRisk"]>(null);

  // this is the tree checkedState; filters need to be applied
  const [selectedHierarchyIdsOverview, setSelectedHierarchyIdsOverview] =
    useState<GlobalFilters["selectedHierarchyIdsOverview"]>({});
  const [selectedHierarchyIdsRisk, setSelectedHierarchyIdsRisk] = useState<
    GlobalFilters["selectedHierarchyIdsRisk"]
  >({});

  const updateHierarchyIdsOverview = useCallback<
    GlobalFilters["updateHierarchyIdsOverview"]
  >((checkedState) => {
    // we only need leaf values which are numbers [Int!]
    const selectedHierarchies = Object.keys(checkedState)
      .filter((key) => {
        return checkedState[key as keyof typeof checkedState] === true;
      })
      .map(Number)
      .filter(removeNodes);
    setHierarchyIdsOverview(selectedHierarchies);
  }, []);

  const updateHierarchyIdsRisk = useCallback<
    GlobalFilters["updateHierarchyIdsRisk"]
  >((checkedState) => {
    // we only need leaf values which are numbers [Int!]
    const selectedHierarchies = Object.keys(checkedState)
      .filter((key) => {
        return checkedState[key as keyof typeof checkedState] === true;
      })
      .map(Number)
      .filter(removeNodes);
    setHierarchyIdsRisk(selectedHierarchies);
  }, []);

  useEffect(() => {
    setSelectedHierarchyIdsOverview(
      initHierarchyIdsCheckedState(
        flattenCheckboxValues(hierarchyCheckboxItemsOverview),
      ),
    );
  }, [hierarchyCheckboxItemsOverview]);
  // #endregion

  useEffect(() => {
    setSelectedHierarchyIdsRisk(
      initHierarchyIdsCheckedState(
        flattenCheckboxValues(hierarchyCheckboxItemsRisk),
      ),
    );
  }, [hierarchyCheckboxItemsRisk]);

  // #region Risk Mitigation
  const riskMitigationQueryResult = useQuery(RiskMitigationsDocument, {
    skip: !isRiskRoute,
    fetchPolicy: "network-only",
  });

  // ? should we store selected Id as well, or auto apply
  const [appliedRiskMitigationId, setAppliedRiskMitigationId] =
    useState<number>(0);
  // #endregion

  // #region Risk Asset Filter
  // used as the basis for selectedRiskAssetFilters; no memoization (mapping) needed
  const assetSelectionFilterQueryResult = useQuery(
    AssetSelectionFilterDocument,
    {
      skip: !isRiskRoute,
    },
  );

  const [initialRiskAssetFiltersSet, setInitialRiskAssetFiltersSet] =
    useState(false);

  const [selectedRiskAssetFilters, setSelectedRiskAssetFilters] =
    useState<RiskAssetFiltersState>(baseRiskAssetFiltersState);

  const [savedRiskAssetFilters, setSavedRiskAssetFilters] =
    useState<RiskAssetFiltersState>(baseRiskAssetFiltersState);

  useEffect(() => {
    if (isRiskRoute) {
      if (assetSelectionFilterQueryResult.data?.assetSelectionFilter) {
        const initialRiskAssetFiltersState = initSelectedRiskAssetFiltersState(
          assetSelectionFilterQueryResult.data.assetSelectionFilter,
        );

        setSelectedRiskAssetFilters(initialRiskAssetFiltersState);
        setSavedRiskAssetFilters(initialRiskAssetFiltersState);

        setInitialRiskAssetFiltersSet(true);
      }
    }
  }, [isRiskRoute, assetSelectionFilterQueryResult.data]);

  const { assetSelectionFilter } = assetSelectionFilterQueryResult.data ?? {};

  const activeFilterCount = useMemo(() => {
    if (assetSelectionFilter) {
      const savedRiskAssetFiltersKeys = Object.keys(
        savedRiskAssetFilters,
      ) as Array<keyof RiskAssetFiltersState>;

      return savedRiskAssetFiltersKeys.reduce((count, key) => {
        // well range tuple slider
        if (key === "wellAgeRange" && assetSelectionFilter.wellAgeRange) {
          count += Number(
            !savedRiskAssetFilters.wellAgeRange.every((age) =>
              assetSelectionFilter.wellAgeRange.includes(age),
            ),
          );
        }

        // checkboxes
        if (
          (key === "mostRecentCorrosionLogs" || key === "wellConfigurations") &&
          assetSelectionFilter.mostRecentCorrosionLogs &&
          assetSelectionFilter.wellConfigurations
        ) {
          count += Number(
            assetSelectionFilter[key].length !==
              savedRiskAssetFilters[key].length,
          );
        }

        // selects (can either have all or single value)
        if (
          (key === "wellTypes" || key === "riskQuartiles") &&
          assetSelectionFilter.riskQuartiles &&
          assetSelectionFilter.wellTypes
        ) {
          count += Number(
            savedRiskAssetFilters[key].length !==
              assetSelectionFilter[key].length,
          );
        }

        return count;
      }, 0);
    }

    return 0;
  }, [assetSelectionFilter, savedRiskAssetFilters]);
  // #endregion

  // #region Well Hierarchy Names
  const [fetchWellNamesByHierarchyLazy, wellNamesByHierarchyQueryResult] =
    useLazyQuery(WellNamesGroupedByHierarchyDocument);

  useEffect(() => {
    if (isRiskRoute) {
      // wait to set the default state from the backend
      if (initialRiskAssetFiltersSet) {
        const {
          mostRecentCorrosionLogs,
          riskQuartiles,
          wellAgeRange,
          wellConfigurations,
          wellTypes,
        } = savedRiskAssetFilters;

        void fetchWellNamesByHierarchyLazy({
          variables: {
            hierarchyIds: hierarchyIdsRisk,
            wellConfigurations: wellConfigurations.length
              ? wellConfigurations
              : undefined,
            mostRecentCorrosionLogs: mostRecentCorrosionLogs.length
              ? mostRecentCorrosionLogs
              : undefined,
            quartileId:
              riskQuartiles.length === 4 ? undefined : riskQuartiles[0].id,
            wellAgeFrom: wellAgeRange[0],
            wellAgeTo: wellAgeRange[1],
            wellType: wellTypes.length === 1 ? wellTypes[0] : undefined,
            isOverall,
          },
        });
      }
    }
  }, [
    fetchWellNamesByHierarchyLazy,
    hierarchyIdsRisk,
    savedRiskAssetFilters,
    initialRiskAssetFiltersSet,
    isRiskRoute,
    isOverall,
  ]);

  const { wellNamesGroupedByHierarchy } =
    wellNamesByHierarchyQueryResult.data ?? {};

  const [hierarchiesWithWellNamesRadios, setHierarchiesWithWellNamesRadios] =
    useState<Array<FilterItemProps>>([]);

  useEffect(() => {
    if (wellNamesGroupedByHierarchy && wellNamesGroupedByHierarchy.length > 0) {
      setHierarchiesWithWellNamesRadios(
        joinHierarchyCheckboxesWithWellIds(
          hierarchyCheckboxItemsRisk,
          wellNamesGroupedByHierarchy.filter(
            (item): item is FilteredWellNamesByHierarchy[number] =>
              typeof item.hierarchyId === "number",
          ),
        ),
      );
    }
  }, [hierarchyCheckboxItemsRisk, wellNamesGroupedByHierarchy]);

  const [radioState, setRadioState] = useState<GlobalFilters["radioState"]>([]);

  const [selectedWellName, setSelectedWellName] = useState<
    GlobalFilters["selectedWellName"]
  >({
    label: "",
    value: "",
  });

  const [appliedWell, setAppliedWell] = useState<[number, string]>([0, ""]);
  const [appliedHierarchyName, setAppliedHierarchyName] = useState<string>("");

  const wellIdNameParam = useMemo(() => {
    const [hierarchyId, wellName] = appliedWell;

    if (!wellName.length) {
      return "";
    }

    return generatePath(":wellIdName", {
      wellIdName: `${hierarchyId}_${wellName}`,
    });
  }, [appliedWell]);

  const onRadioChange = useCallback(
    (
      selection: GlobalFilters["selectedWellName"] | undefined,
      radioState: GlobalFilters["radioState"] | undefined,
    ) => {
      if (radioState) {
        const flatRadioState = flattenRadioState(radioState);

        // no wellName node; top 2 hierarchies are not clickable
        if (flatRadioState.length === 3) {
          const breadcrumbs = deriveRadioTreeBreadcrumbs(
            hierarchiesWithWellNamesRadios,
            flatRadioState.at(-1)!.value,
          );

          // default to the first well and regenerate state
          setSelectedWellName(breadcrumbs.at(-1) ?? { label: "", value: "" });
          setRadioState(generateRadioState(breadcrumbs));
        } else {
          setSelectedWellName(selection ?? { label: "", value: "" });
          setRadioState(radioState ?? []);
        }
      }
    },
    [hierarchiesWithWellNamesRadios],
  );

  useEffect(() => {
    const flattenedRadioState = flattenRadioState(radioState);

    const wellNode = flattenedRadioState.find(
      (node) => node.value === `${appliedWell[0]}_${appliedWell[1]}`,
    );

    if (wellNode) {
      const hierarchyNode =
        flattenedRadioState[flattenedRadioState.indexOf(wellNode) - 1];
      if (hierarchyNode) {
        setAppliedHierarchyName(hierarchyNode.label);
      }
    }
  }, [radioState, appliedWell]);

  const [hierarchyId, wellName] = appliedWell;

  const riskComparisonOverallQueryResult = useQuery(
    RiskManagementOverallComparisonChartDocument,
    {
      skip: !isOverallRoute || !wellName,
      variables: {
        hierarchyId,
        wellName,
        isBaseline: true,
        riskMitigationTypeId: appliedRiskMitigationId,
      },
    },
  );

  const riskComparisonMajorEventQueryResult = useQuery(
    RiskManagementMajorEventComparisonChartDocument,
    {
      skip: !isMajorEventRoute || !wellName,
      variables: {
        hierarchyId,
        wellName,
        isBaseline: true,
      },
    },
  );

  const riskManagementOverallComparisonChart =
    riskComparisonOverallQueryResult.data?.riskManagementOverallComparisonChart;
  const overallOperationalRiskByWellName =
    riskManagementOverallComparisonChart?.operationalRiskByWellName ?? 0;
  const overallInterventionRiskByWellName =
    riskManagementOverallComparisonChart?.interventionRiskByWellName ?? 0;

  const riskManagementMajorEventComparisonChart =
    riskComparisonMajorEventQueryResult.data
      ?.riskManagementMajorEventComparisonChart;
  const majorEventOperationalRiskByWellName =
    riskManagementMajorEventComparisonChart?.operationalRiskByWellName ?? 0;
  const majorEventInterventionRiskByWellName =
    riskManagementMajorEventComparisonChart?.interventionRiskByWellName ?? 0;
  // #endregion

  const value = useMemo<GlobalFilters>(
    () => ({
      hierarchyIdsOverview,
      hierarchyIdsRisk,
      selectedHierarchyIdsOverview,
      selectedHierarchyIdsRisk,
      setSelectedHierarchyIdsOverview,
      setSelectedHierarchyIdsRisk,
      updateHierarchyIdsOverview,
      updateHierarchyIdsRisk,
      hierarchyCheckboxItemsOverview,
      hierarchyCheckboxItemsRisk,
      hierarchiesWithWellNamesRadios,
      radioState,
      setRadioState,
      selectedWellName,
      setSelectedWellName,
      appliedHierarchyName,
      appliedWell,
      setAppliedWell,
      selectedRiskAssetFilters,
      setSelectedRiskAssetFilters,
      savedRiskAssetFilters,
      setSavedRiskAssetFilters,
      activeFilterCount,
      appliedRiskMitigationId,
      setAppliedRiskMitigationId,
      riskMitigationQueryResult,
      assetSelectionQueryResult,
      assetSelectionFilterQueryResult,
      wellNamesByHierarchyQueryResult,
      overallOperationalRiskByWellName,
      overallInterventionRiskByWellName,
      majorEventOperationalRiskByWellName,
      majorEventInterventionRiskByWellName,
      onRadioChange,
      wellIdNameParam,
    }),
    [
      hierarchyIdsOverview,
      hierarchyIdsRisk,
      selectedHierarchyIdsOverview,
      selectedHierarchyIdsRisk,
      setSelectedHierarchyIdsOverview,
      setSelectedHierarchyIdsRisk,
      updateHierarchyIdsOverview,
      updateHierarchyIdsRisk,
      hierarchyCheckboxItemsOverview,
      hierarchyCheckboxItemsRisk,
      hierarchiesWithWellNamesRadios,
      radioState,
      setRadioState,
      selectedWellName,
      setSelectedWellName,
      appliedHierarchyName,
      appliedWell,
      setAppliedWell,
      selectedRiskAssetFilters,
      setSelectedRiskAssetFilters,
      savedRiskAssetFilters,
      setSavedRiskAssetFilters,
      activeFilterCount,
      appliedRiskMitigationId,
      setAppliedRiskMitigationId,
      riskMitigationQueryResult,
      assetSelectionQueryResult,
      assetSelectionFilterQueryResult,
      wellNamesByHierarchyQueryResult,
      overallOperationalRiskByWellName,
      overallInterventionRiskByWellName,
      majorEventOperationalRiskByWellName,
      majorEventInterventionRiskByWellName,
      onRadioChange,
      wellIdNameParam,
    ],
  );

  return (
    <GlobalFiltersContext.Provider value={value}>
      {children}
    </GlobalFiltersContext.Provider>
  );
};

export default GlobalFiltersProvider;
